import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  Image as ImageIcon,
  Video,
  MapPin,
  Hash,
  Users,
  Globe,
  Lock,
  ChevronRight,
  ChevronDown,
  UserCheck,
  UsersRound,
  Pencil,
  Search,
  Loader2,
  Navigation,
} from 'lucide-react';
import type { PetForSocial, SharedPlatform, SocialPost } from './socialTypes';
import { PETSO_CTA_TEXT } from './crossPostConstants';
import { MediaEditorModal } from './MediaEditorModal';
import { initSocialMediaUpload, uploadSocialMedia, getTaggablePets } from '@/api/social';
import { getApiErrorMessage } from '@/api/axios';
import { toast } from 'sonner';

/** Payload when a post is created (owner-based; taggedPetIds optional) */
export interface NewPostPayload {
  ownerId: string;
  ownerName: string;
  ownerAvatar?: string;
  taggedPetIds: string[];
  caption: string;
  images: string[];
  /** When true, the post's first media item is video (for "Also share to Your Story"). */
  isVideo?: boolean;
  location?: string;
  fromPetParks?: boolean;
  /** When true, the post's first image/video is also added to Your Story. */
  alsoShareToStory?: boolean;
  /** Platforms to cross-post to (Instagram, Facebook, TikTok, X). CTA appended per platform. */
  sharedPlatforms?: SharedPlatform[];
  /** Caption with CTA for external platforms (used when cross-posting). */
  crossPostCaption?: string;
  visibility:
    | 'public'
    | 'followers'
    | 'friends'
    | 'friends-of-friends'
    | 'private';
}

interface CreatePostProps {
  userType: 'owner' | 'professional';
  userId: string;
  ownerName?: string;
  /** Current user's avatar URL (e.g. from Clerk) so the post is attributed with the correct photo. */
  ownerAvatar?: string;
  pets: PetForSocial[];
  onClose: () => void;
  onPostCreated: (payload: NewPostPayload) => void;
  /** Prefill location (e.g. park name when sharing from Pet Parks) */
  initialLocation?: string;
  /** Prefill caption text (e.g. when joining a challenge hashtag). */
  initialCaption?: string;
  /** When set, opens in edit mode (skips media selection step and pre-fills details). */
  editingPost?: Pick<
    SocialPost,
    'id' | 'images' | 'caption' | 'taggedPetIds' | 'location'
  > & { visibility?: NewPostPayload['visibility'] };
  /** Called when post update is saved (edit mode only). */
  onPostUpdated?: (
    id: string,
    updates: {
      caption: string;
      taggedPetIds: string[];
      images: string[];
      location?: string;
      visibility: NewPostPayload['visibility'];
    }
  ) => void;
}

export function CreatePost({
  userType,
  userId,
  ownerName = 'You',
  ownerAvatar,
  pets,
  onClose,
  onPostCreated,
  initialLocation,
  initialCaption,
  editingPost,
  onPostUpdated,
}: CreatePostProps) {
  const { t } = useTranslation();
  const isEditing = !!editingPost;
  const [step, setStep] = useState<'content' | 'details'>(() =>
    isEditing ? 'details' : 'content'
  );
  const [petsDropdownOpen, setPetsDropdownOpen] = useState(false);
  const petsDropdownRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  /** Object URLs for selected images or video (for preview) */
  const [selectedMediaUrls, setSelectedMediaUrls] = useState<string[]>([]);
  /** Actual File objects for upload to server (kept in sync with selectedMediaUrls) */
  const selectedFilesRef = useRef<File[]>([]);
  /** Ref for the location input container – used to close suggestion dropdown on outside click */
  const locationContainerRef = useRef<HTMLDivElement>(null);
  /** Debounce timer for location search */
  const locationDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const [uploading, setUploading] = useState(false);
  /** In edit mode: existing CDN image URLs/keys from the post (kept unless user picks new photos). */
  const [existingImageUrls, setExistingImageUrls] = useState<string[]>(
    () => editingPost?.images ?? []
  );

  // Initialize fields from editingPost when opened in edit mode
  useEffect(() => {
    if (!editingPost) return;
    setCaption(editingPost.caption ?? '');
    setSelectedPets(editingPost.taggedPetIds ?? []);
    setLocation(editingPost.location ?? '');
    setVisibility(
      (editingPost.visibility as NewPostPayload['visibility']) ?? 'public'
    );
    setExistingImageUrls(editingPost.images ?? []);
  }, [editingPost?.id]);

  useEffect(() => {
    if (!petsDropdownOpen) return;
    const close = (e: MouseEvent) => {
      if (
        petsDropdownRef.current &&
        !petsDropdownRef.current.contains(e.target as Node)
      ) {
        setPetsDropdownOpen(false);
      }
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [petsDropdownOpen]);

  const [contentType, setContentType] = useState<'photo' | 'video' | 'text'>(
    'photo'
  );
  const [selectedPets, setSelectedPets] = useState<string[]>([]);
  const [caption, setCaption] = useState(initialCaption ?? '');
  const [location, setLocation] = useState(initialLocation ?? '');
  const [locationSuggestions, setLocationSuggestions] = useState<string[]>([]);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);
  const [geoDetecting, setGeoDetecting] = useState(false);

  useEffect(() => {
    if (!locationDropdownOpen) return;
    const close = (e: MouseEvent) => {
      if (
        locationContainerRef.current &&
        !locationContainerRef.current.contains(e.target as Node)
      ) {
        setLocationDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [locationDropdownOpen]);

  useEffect(() => {
    if (initialLocation !== undefined) setLocation(initialLocation);
  }, [initialLocation]);

  useEffect(() => {
    if (initialCaption !== undefined) {
      setCaption(initialCaption);
    }
  }, [initialCaption]);

  // When opened from Pet Parks "Also share to Social?" – reinforce location and clear stored share data
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('petsoShareFromPark');
      if (raw) {
        const data = JSON.parse(raw) as {
          parkName?: string;
          parkId?: string;
          imageUrl?: string;
        };
        sessionStorage.removeItem('petsoShareFromPark');
        if (data.parkName) setLocation((prev) => prev || data.parkName!);
      }
    } catch {
      // ignore
    }
  }, []);

  const [visibility, setVisibility] = useState<
    'public' | 'followers' | 'friends' | 'friends-of-friends' | 'private'
  >('public');
  const [alsoShareToStory, setAlsoShareToStory] = useState(false);
  /** Cross-post: which connected platforms to publish to (opt-in per post). */
  const [selectedSharedPlatforms] = useState<SharedPlatform[]>([]);
  /** Index of photo being edited; null = editor closed */
  const [editingMediaIndex, setEditingMediaIndex] = useState<number | null>(
    null
  );

  /** Taggable pets: own + followers' pets (fetched when on details step). */
  const [taggablePets, setTaggablePets] = useState<PetForSocial[]>([]);
  const [taggablePetsLoading, setTaggablePetsLoading] = useState(false);
  const [petSearchQuery, setPetSearchQuery] = useState('');

  useEffect(() => {
    if (step !== 'details') return;
    let cancelled = false;
    setTaggablePetsLoading(true);
    getTaggablePets()
      .then(({ pets: list }) => {
        if (cancelled) return;
        const apiPets = list.map((p) => ({
          id: p.id,
          name: p.ownerName ? `${p.name} (${p.ownerName})` : p.name,
          species: p.species,
          avatar: p.avatar,
          imageUrl: p.imageUrl,
          photoKey: p.photoKey,
        }));
        // Merge: own pets first (from props), then API results that aren't duplicates
        const ownIds = new Set(pets.map((p) => p.id));
        const extra = apiPets.filter((p) => !ownIds.has(p.id));
        setTaggablePets([...pets, ...extra]);
      })
      .catch(() => {
        if (!cancelled) setTaggablePets(pets);
      })
      .finally(() => {
        if (!cancelled) setTaggablePetsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [step, pets]);

  const searchLower = petSearchQuery.trim().toLowerCase();
  const displayPets = searchLower
    ? taggablePets.filter((p) => p.name.toLowerCase().includes(searchLower))
    : taggablePets;

  const togglePet = (petId: string) => {
    if (selectedPets.includes(petId)) {
      setSelectedPets(selectedPets.filter((id) => id !== petId));
    } else {
      setSelectedPets([...selectedPets, petId]);
    }
  };

  const acceptFileTypes = contentType === 'photo' ? 'image/*' : 'video/*';
  const isMultiple = contentType === 'photo';

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = e.target.files;
      if (!fileList?.length) return;
      const filesArray = Array.from(fileList);
      const count =
        contentType === 'video' ? 1 : Math.min(filesArray.length, 10);
      const filesToUse = filesArray.slice(0, count);
      const newUrls = filesToUse.map((f) => URL.createObjectURL(f));
      selectedFilesRef.current = filesToUse;
      setSelectedMediaUrls((prev) => {
        prev.forEach((url) => URL.revokeObjectURL(url));
        return newUrls;
      });
      setTimeout(() => {
        if (fileInputRef.current) fileInputRef.current.value = '';
      }, 0);
    },
    [contentType]
  );

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  /** Search Nominatim for location suggestions as the user types */
  const searchLocations = useCallback(async (query: string) => {
    if (!query.trim()) {
      setLocationSuggestions([]);
      return;
    }
    setLocationLoading(true);
    try {
      const resp = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=5&addressdetails=1`,
        { headers: { 'Accept-Language': 'en' } }
      );
      if (!resp.ok) return;
      const results: Array<{
        display_name: string;
        name?: string;
        address?: Record<string, string>;
      }> = await resp.json();
      const places = results.map((r) => {
        const addr = r.address ?? {};
        const parts: string[] = [];
        const firstName = r.name || r.display_name.split(',')[0].trim();
        parts.push(firstName);
        const city = addr.city ?? addr.town ?? addr.village ?? '';
        if (city && !firstName.includes(city)) parts.push(city);
        if (addr.country) parts.push(addr.country);
        return [...new Set(parts)].join(', ');
      });
      setLocationSuggestions([...new Set(places)].filter(Boolean));
    } catch {
      // ignore network errors
    } finally {
      setLocationLoading(false);
    }
  }, []);

  /** Reverse-geocode current position and populate suggestions (shown on field focus) */
  const detectNearby = useCallback(() => {
    if (!navigator.geolocation) return;
    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const resp = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${pos.coords.latitude}&lon=${pos.coords.longitude}&format=json&zoom=14`,
            { headers: { 'Accept-Language': 'en' } }
          );
          if (!resp.ok) return;
          const data: {
            display_name?: string;
            address?: Record<string, string>;
          } = await resp.json();
          const addr = data.address ?? {};
          const city = addr.city ?? addr.town ?? addr.village ?? '';
          const country = addr.country ?? '';
          const suggestions: string[] = [];
          if (addr.suburb || addr.neighbourhood) {
            suggestions.push(
              [addr.suburb ?? addr.neighbourhood, city, country]
                .filter(Boolean)
                .join(', ')
            );
          }
          if (city) {
            suggestions.push(
              [city, addr.state, country].filter(Boolean).join(', ')
            );
          }
          if (data.display_name) {
            suggestions.push(
              data.display_name.split(',').slice(0, 3).join(',').trim()
            );
          }
          setLocationSuggestions(
            [...new Set(suggestions)].filter(Boolean).slice(0, 4)
          );
        } catch {
          setLocationSuggestions([]);
        } finally {
          setLocationLoading(false);
        }
      },
      () => {
        setLocationLoading(false);
      },
      { timeout: 8000 }
    );
  }, []);

  /** Detect current position and set the location field directly (via GPS button) */
  const detectAndSetLocation = useCallback(() => {
    if (!navigator.geolocation) return;
    setGeoDetecting(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const resp = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${pos.coords.latitude}&lon=${pos.coords.longitude}&format=json&zoom=12`,
            { headers: { 'Accept-Language': 'en' } }
          );
          if (!resp.ok) return;
          const data: {
            display_name?: string;
            address?: Record<string, string>;
          } = await resp.json();
          const addr = data.address ?? {};
          const city = addr.city ?? addr.town ?? addr.village ?? '';
          const place =
            [city, addr.state, addr.country].filter(Boolean).join(', ') ||
            data.display_name?.split(',').slice(0, 2).join(',').trim() ||
            '';
          if (place) {
            setLocation(place);
            setLocationDropdownOpen(false);
            setLocationSuggestions([]);
          }
        } catch {
          // ignore
        } finally {
          setGeoDetecting(false);
        }
      },
      () => {
        setGeoDetecting(false);
      },
      { timeout: 8000 }
    );
  }, []);

  const handleMediaEditDone = useCallback(
    (index: number) => (file: File) => {
      selectedFilesRef.current[index] = file;
      const newUrl = URL.createObjectURL(file);
      setSelectedMediaUrls((prev) => {
        const old = prev[index];
        if (old) URL.revokeObjectURL(old);
        const next = [...prev];
        next[index] = newUrl;
        return next;
      });
      setEditingMediaIndex(null);
    },
    []
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const files = Array.from(e.dataTransfer.files).filter((f) =>
        contentType === 'photo'
          ? f.type.startsWith('image/')
          : f.type.startsWith('video/')
      );
      if (!files.length) return;
      const filesToUse =
        contentType === 'video' ? [files[0]] : files.slice(0, 10);
      selectedFilesRef.current = filesToUse;
      setSelectedMediaUrls((prev) => {
        prev.forEach((url) => URL.revokeObjectURL(url));
        return filesToUse.map((f) => URL.createObjectURL(f));
      });
    },
    [contentType]
  );

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Clear media when content type changes
  useEffect(() => {
    selectedFilesRef.current = [];
    setSelectedMediaUrls((prev) => {
      prev.forEach((url) => URL.revokeObjectURL(url));
      return [];
    });
  }, [contentType]);

  const handlePost = async () => {
    if (contentType === 'photo' && selectedMediaUrls.length === 0) {
      toast.error(t('social.toast.addPhotoBeforePost'));
      return;
    }
    const sharedPlatforms =
      selectedSharedPlatforms.length > 0 ? selectedSharedPlatforms : undefined;
    const crossPostCaption =
      sharedPlatforms && sharedPlatforms.length > 0
        ? `${caption.trim() ? caption.trim() + '\n\n' : ''}${PETSO_CTA_TEXT}`
        : undefined;

    let imageKeysOrUrls: string[] = [];
    const files = selectedFilesRef.current;

    if (files.length > 0) {
      setUploading(true);
      try {
        for (const file of files) {
          const contentTypeHeader = file.type || 'application/octet-stream';
          const { uploadUrl, key } = await initSocialMediaUpload({
            filename: file.name,
            contentType: contentTypeHeader,
          });
          await uploadSocialMedia(uploadUrl, file);
          imageKeysOrUrls.push(key);
        }
      } catch (err) {
        console.error('Social post upload failed:', err);
        const message =
          err instanceof Error && err.message.startsWith('Upload failed:')
            ? t('social.toast.uploadFailed')
            : getApiErrorMessage(err, t('social.toast.uploadFailedStart'));
        toast.error(message);
        setUploading(false);
        return; // Do not create post with blob URLs – they disappear when switching features or after a while
      } finally {
        setUploading(false);
      }
    }

    const payload: NewPostPayload = {
      ownerId: userId,
      ownerName,
      ownerAvatar: ownerAvatar ?? undefined,
      taggedPetIds: selectedPets,
      caption,
      images: imageKeysOrUrls,
      isVideo: contentType === 'video',
      location: location || undefined,
      fromPetParks: !!initialLocation,
      alsoShareToStory,
      sharedPlatforms,
      crossPostCaption,
      visibility,
    };
    onPostCreated(payload);
  };

  /** Save changes in edit mode – uploads new files if any, otherwise keeps existing images. */
  const handleUpdate = async () => {
    if (!editingPost || !onPostUpdated) return;
    let images: string[];
    const files = selectedFilesRef.current;
    if (files.length > 0) {
      setUploading(true);
      const newKeys: string[] = [];
      try {
        for (const file of files) {
          const contentTypeHeader = file.type || 'application/octet-stream';
          const { uploadUrl, key } = await initSocialMediaUpload({
            filename: file.name,
            contentType: contentTypeHeader,
          });
          await uploadSocialMedia(uploadUrl, file);
          newKeys.push(key);
        }
      } catch (err) {
        console.error('Social post edit upload failed:', err);
        toast.error(t('social.toast.uploadFailed'));
        setUploading(false);
        return;
      } finally {
        setUploading(false);
      }
      images = newKeys;
    } else {
      images = existingImageUrls;
    }
    onPostUpdated(editingPost.id, {
      caption,
      taggedPetIds: selectedPets,
      images,
      location: location || undefined,
      visibility,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-[#16151D] rounded-2xl border dark:border-white/8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sticky top-0 bg-white dark:bg-[#16151D] z-10 rounded-t-2xl border-b border-[#E2DFEA] dark:border-white/8">
          <h2
            className="text-xl text-[#2D2F7E] dark:text-white"
            style={{
              fontFamily: 'Sora, sans-serif',
              fontWeight: 700,
              letterSpacing: '-0.02em',
            }}
          >
            {isEditing
              ? t('social.editPost', 'Edit Post')
              : t('social.createPostModal.title')}
          </h2>
          <button
            onClick={onClose}
            className="hover:bg-[#F0EFF5] dark:hover:bg-white/5 p-2 rounded-lg transition-colors text-[#464452] dark:text-white/70"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Step 1: Choose Content Type */}
          {step === 'content' && (
            <>
              <div>
                <h3
                  className="mb-4 text-[#2D2F7E] dark:text-white"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  {t('social.createPostModal.whatToShare')}
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <button
                    onClick={() => setContentType('photo')}
                    className={`p-6 rounded-2xl border-2 transition-all ${
                      contentType === 'photo'
                        ? 'border-[#936FEF] bg-[#F0EFF5] dark:bg-purple-900/20'
                        : 'border-[#E2DFEA] dark:border-white/20'
                    }`}
                  >
                    <ImageIcon className="w-8 h-8 mx-auto mb-2 text-[#936FEF]" />
                    <div
                      className="font-medium text-[#2D2F7E] dark:text-white"
                      style={{ fontFamily: 'Sora, sans-serif' }}
                    >
                      {t('social.createPostModal.photo')}
                    </div>
                    <div
                      className="text-sm text-[#464452] dark:text-white/70"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {t('social.createPostModal.shareMoment')}
                    </div>
                  </button>

                  <button
                    onClick={() => setContentType('video')}
                    className={`p-6 rounded-2xl border-2 transition-all ${
                      contentType === 'video'
                        ? 'border-[#936FEF] bg-[#F0EFF5] dark:bg-purple-900/20'
                        : 'border-[#E2DFEA] dark:border-white/20'
                    }`}
                  >
                    <Video className="w-8 h-8 mx-auto mb-2 text-[#936FEF]" />
                    <div
                      className="font-medium text-[#2D2F7E] dark:text-white"
                      style={{ fontFamily: 'Sora, sans-serif' }}
                    >
                      {t('social.createPostModal.video')}
                    </div>
                    <div
                      className="text-sm text-[#464452] dark:text-white/70"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {t('social.createPostModal.shareClip')}
                    </div>
                  </button>

                  <button
                    onClick={() => setContentType('text')}
                    className={`p-6 rounded-2xl border-2 transition-all ${
                      contentType === 'text'
                        ? 'border-[#936FEF] bg-[#F0EFF5] dark:bg-purple-900/20'
                        : 'border-[#E2DFEA] dark:border-white/20'
                    }`}
                  >
                    <Hash className="w-8 h-8 mx-auto mb-2 text-[#936FEF]" />
                    <div
                      className="font-medium text-[#2D2F7E] dark:text-white"
                      style={{ fontFamily: 'Sora, sans-serif' }}
                    >
                      {t('social.createPostModal.text')}
                    </div>
                    <div
                      className="text-sm text-[#464452] dark:text-white/70"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {t('social.createPostModal.shareUpdate')}
                    </div>
                  </button>
                </div>
              </div>

              {/* Upload Area */}
              <input
                ref={fileInputRef}
                type="file"
                accept={acceptFileTypes}
                multiple={isMultiple}
                className="hidden"
                onChange={handleFileChange}
                aria-label={t('social.createPostModal.uploadFile')}
              />
              <div
                role="button"
                tabIndex={0}
                onClick={contentType !== 'text' ? openFileDialog : undefined}
                onKeyDown={
                  contentType !== 'text'
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openFileDialog();
                        }
                      }
                    : undefined
                }
                onDrop={contentType !== 'text' ? handleDrop : undefined}
                onDragOver={contentType !== 'text' ? handleDragOver : undefined}
                className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-default ${
                  contentType !== 'text'
                    ? 'border-[#E2DFEA] dark:border-white/20 cursor-pointer hover:border-[#936FEF] dark:hover:border-purple-500/50'
                    : 'border-[#E2DFEA] dark:border-white/20'
                }`}
              >
                {contentType === 'text' ? (
                  <div
                    className="text-left w-full"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <label
                      className="block text-sm mb-2 text-[#464452] dark:text-white/70"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {t('social.createPostModal.shareUpdate')}
                    </label>
                    <textarea
                      value={caption}
                      onChange={(e) => setCaption(e.target.value)}
                      placeholder={t('social.createPostModal.placeholderMind')}
                      className="w-full px-4 py-3 rounded-xl resize-none border border-[#E2DFEA] dark:border-white/20 bg-white dark:bg-[#0F0E16] text-[#464452] dark:text-white/90 placeholder:text-[#464452]/50 dark:placeholder:text-white/50 min-h-[140px]"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                      rows={5}
                      aria-label={t('social.createPostModal.writePostAria')}
                    />
                  </div>
                ) : selectedMediaUrls.length > 0 ? (
                  <div
                    className="space-y-4"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    role="presentation"
                  >
                    {/* Large main preview so user clearly sees selected photo(s) – prevent clicks from opening file picker */}
                    <div className="rounded-xl overflow-hidden bg-[#F0EFF5] dark:bg-white/10 min-h-[200px] max-h-[320px] flex items-center justify-center relative">
                      {contentType === 'photo' ? (
                        selectedMediaUrls.length === 1 ? (
                          <div className="relative w-full h-full flex items-center justify-center">
                            <img
                              src={selectedMediaUrls[0]}
                              alt={t('social.createPostModal.previewAlt')}
                              className="w-full h-full object-contain max-h-[320px]"
                            />
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setEditingMediaIndex(0);
                              }}
                              className="absolute bottom-3 right-3 flex items-center gap-2 px-3 py-2 rounded-xl bg-black/60 hover:bg-black/80 text-white text-sm font-medium"
                            >
                              <Pencil className="w-4 h-4" />
                              {t('social.cropAndEdit')}
                            </button>
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-2 justify-center p-4">
                            {selectedMediaUrls.map((url, i) => (
                              <div
                                key={`${url}-${i}`}
                                className="relative w-24 h-24 rounded-lg overflow-hidden shrink-0 group"
                              >
                                <img
                                  src={url}
                                  alt={t('social.previewN', { n: i + 1 })}
                                  className="w-full h-full object-cover"
                                />
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    setEditingMediaIndex(i);
                                  }}
                                  className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg"
                                >
                                  <Pencil className="w-6 h-6 text-white" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )
                      ) : (
                        <video
                          src={selectedMediaUrls[0]}
                          className="max-h-[320px] object-contain"
                          muted
                          playsInline
                          controls
                        />
                      )}
                    </div>
                    <p
                      className="text-sm text-[#464452] dark:text-white/70 text-center"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {selectedMediaUrls.length}{' '}
                      {selectedMediaUrls.length !== 1
                        ? t('social.filesSelectedPlural')
                        : t('social.filesSelectedSingle')}
                    </p>
                  </div>
                ) : (
                  <>
                    <ImageIcon className="w-12 h-12 mx-auto mb-4 text-[#464452] dark:text-white/40 opacity-40" />
                    <p
                      className="mb-2 text-[#464452] dark:text-white/70"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {contentType === 'photo' &&
                        t('social.createPostModal.dropPhotos')}
                      {contentType === 'video' &&
                        t('social.createPostModal.dropVideo')}
                    </p>
                  </>
                )}
                {contentType !== 'text' && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openFileDialog();
                    }}
                    className="mt-4 px-4 py-2 rounded-xl text-white hover:opacity-90 transition-opacity"
                    style={{
                      background:
                        'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
                      fontFamily: 'Sora, sans-serif',
                      fontWeight: 700,
                    }}
                  >
                    {t('social.createPostModal.browseFiles')}
                  </button>
                )}
              </div>

              <button
                onClick={() => {
                  if (
                    contentType === 'photo' &&
                    selectedMediaUrls.length === 0
                  ) {
                    toast.error(t('social.createPostModal.addPhotoFirst'));
                    return;
                  }
                  setStep('details');
                }}
                className="w-full px-6 py-3 rounded-xl text-white hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background:
                    'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
                  fontFamily: 'Sora, sans-serif',
                  fontWeight: 700,
                }}
                disabled={
                  contentType === 'photo' && selectedMediaUrls.length === 0
                }
              >
                {t('social.createPostModal.nextAddDetails')}
                <ChevronRight className="w-4 h-4" />
              </button>
            </>
          )}

          {/* Step 2: Add Details (caption, location, visibility) with Tag Pets dropdown at top */}
          {step === 'details' && (
            <>
              {/* Show selected photo(s) at top so user clearly sees what they're posting */}
              {(() => {
                // In edit mode show existing images if no new files are selected yet
                const previewUrls =
                  isEditing && selectedMediaUrls.length === 0
                    ? existingImageUrls
                    : selectedMediaUrls;
                return previewUrls.length > 0 ? (
                  <div className="rounded-xl overflow-hidden bg-[#F0EFF5] dark:bg-white/10 mb-6">
                    {contentType === 'photo' || isEditing ? (
                      previewUrls.length === 1 ? (
                        <img
                          src={previewUrls[0]}
                          alt={t('social.yourPost')}
                          className="w-full max-h-[280px] object-contain"
                        />
                      ) : (
                        <div className="flex gap-2 p-3 overflow-x-auto">
                          {previewUrls.map((url, i) => (
                            <img
                              key={url}
                              src={url}
                              alt={t('social.photoN', { n: i + 1 })}
                              className="h-32 w-32 rounded-lg object-cover shrink-0"
                            />
                          ))}
                        </div>
                      )
                    ) : (
                      <video
                        src={previewUrls[0]}
                        className="w-full max-h-[280px] object-contain"
                        muted
                        playsInline
                        controls
                      />
                    )}
                    <p
                      className="text-xs text-[#464452] dark:text-white/60 py-2 px-3 text-center"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {t('social.thisIsWhatYouAreSharing')}
                    </p>
                  </div>
                ) : null;
              })()}
              {/* In edit mode: allow user to select replacement photos */}
              {isEditing && (
                <div className="mb-4">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handleFileChange}
                    aria-label={t('social.changePhoto')}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl border border-[#E2DFEA] dark:border-white/20 text-[#464452] dark:text-white/70 hover:bg-[#F0EFF5] dark:hover:bg-white/5 text-sm"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    <ImageIcon className="w-4 h-4" />
                    {selectedMediaUrls.length > 0
                      ? t(
                          'social.photosSelected',
                          '{{n}} new photo(s) selected',
                          { n: selectedMediaUrls.length }
                        )
                      : t('social.changePhoto')}
                  </button>
                </div>
              )}
              <div className="space-y-4">
                {/* Tag Pet(s) — dropdown list */}
                <div>
                  <label
                    className="block text-sm mb-2 text-[#464452] dark:text-white/70"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('social.tagPets')}{' '}
                    <span className="text-[#464452]/60 dark:text-white/50">
                      ({t('common.optional')})
                    </span>
                  </label>
                  {taggablePetsLoading && taggablePets.length === 0 ? (
                    <div className="rounded-xl border border-[#E2DFEA] dark:border-white/20 p-4 text-center">
                      <p
                        className="text-[#464452] dark:text-white/70 text-sm"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {t('social.loadingPets')}
                      </p>
                    </div>
                  ) : displayPets.length === 0 && !taggablePetsLoading ? (
                    <div className="rounded-xl border border-[#E2DFEA] dark:border-white/20 p-4 text-center">
                      <p
                        className="text-[#464452] dark:text-white/70 text-sm"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {petSearchQuery.trim()
                          ? t('social.noPetsMatchSearch')
                          : t('social.addOrFollowPetsToTag')}
                      </p>
                    </div>
                  ) : (
                    <div className="relative" ref={petsDropdownRef}>
                      <button
                        type="button"
                        onClick={() => setPetsDropdownOpen((o) => !o)}
                        className="w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl border border-[#E2DFEA] dark:border-white/20 bg-white dark:bg-[#0F0E16] text-left text-[#464452] dark:text-white/90"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        <span className="truncate">
                          {selectedPets.length === 0
                            ? t('social.createPostModal.general')
                            : taggablePets
                                .filter((p) => selectedPets.includes(p.id))
                                .map((p) => p.name)
                                .join(', ')}
                        </span>
                        <ChevronDown
                          className={`w-5 h-5 flex-shrink-0 text-[#464452] dark:text-white/50 transition-transform ${
                            petsDropdownOpen ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                      {petsDropdownOpen && (
                        <div
                          className="absolute top-full left-0 right-0 mt-1 rounded-xl border border-[#E2DFEA] dark:border-white/20 bg-white dark:bg-[#16151D] shadow-lg z-20 max-h-72 overflow-hidden flex flex-col"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          <div className="p-2 border-b border-[#E2DFEA] dark:border-white/10 shrink-0">
                            <div className="relative">
                              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#464452] dark:text-white/40 pointer-events-none" />
                              <input
                                type="text"
                                value={petSearchQuery}
                                onChange={(e) =>
                                  setPetSearchQuery(e.target.value)
                                }
                                placeholder={t('social.searchByPetName')}
                                className="w-full pl-9 pr-3 py-2 rounded-lg border border-[#E2DFEA] dark:border-white/20 bg-white dark:bg-[#0F0E16] text-[#2D2F7E] dark:text-white text-sm placeholder:text-[#464452]/60 dark:placeholder:text-white/50"
                                style={{ fontFamily: 'Inter, sans-serif' }}
                              />
                            </div>
                          </div>
                          <div className="overflow-y-auto max-h-52">
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedPets([]);
                                setPetsDropdownOpen(false);
                              }}
                              className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#F0EFF5] dark:hover:bg-white/5 border-b border-[#E2DFEA] dark:border-white/10 ${
                                selectedPets.length === 0
                                  ? 'bg-[#F0EFF5] dark:bg-purple-900/20'
                                  : ''
                              }`}
                            >
                              <div className="w-9 h-9 rounded-full flex items-center justify-center text-lg bg-[#F0EFF5] dark:bg-white/10">
                                #
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-[#2D2F7E] dark:text-white truncate">
                                  {t('social.createPostModal.general')}
                                </div>
                                <div className="text-xs text-[#464452] dark:text-white/60 truncate">
                                  {t('social.createPostModal.noPetTagged')}
                                </div>
                              </div>
                              {selectedPets.length === 0 && (
                                <span className="text-[#936FEF] font-bold">
                                  ✓
                                </span>
                              )}
                            </button>
                            {displayPets.map((pet) => (
                              <button
                                key={pet.id}
                                type="button"
                                onClick={() => {
                                  togglePet(pet.id);
                                  setPetsDropdownOpen(false);
                                }}
                                className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#F0EFF5] dark:hover:bg-white/5 ${
                                  selectedPets.includes(pet.id)
                                    ? 'bg-[#F0EFF5] dark:bg-purple-900/20'
                                    : ''
                                }`}
                              >
                                <div className="w-9 h-9 rounded-full flex items-center justify-center text-lg bg-[#F0EFF5] dark:bg-white/10">
                                  {pet.avatar}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-[#2D2F7E] dark:text-white truncate">
                                    {pet.name}
                                  </div>
                                  <div className="text-xs text-[#464452] dark:text-white/60 truncate capitalize">
                                    {t(`petOnboarding.step1.${pet.species}`, pet.species)}
                                  </div>
                                </div>
                                {selectedPets.includes(pet.id) && (
                                  <span className="text-[#936FEF] font-bold">
                                    ✓
                                  </span>
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Caption */}
                <div>
                  <label
                    className="block text-sm mb-2 text-[#464452] dark:text-white/70"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('social.caption')}
                  </label>
                  <textarea
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    placeholder={t('social.createPostModal.writeCaption')}
                    className="w-full px-4 py-3 rounded-xl resize-none border border-[#E2DFEA] dark:border-white/20 bg-white dark:bg-[#0F0E16] text-[#464452] dark:text-white/90 placeholder:text-[#464452]/50 dark:placeholder:text-white/50"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                    rows={4}
                  />
                </div>

                {/* Location */}
                <div ref={locationContainerRef}>
                  <label
                    className="block text-sm mb-2 text-[#464452] dark:text-white/70"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('social.addLocationOptional')}
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#464452] dark:text-white/40 opacity-40 pointer-events-none" />
                    <input
                      type="text"
                      value={location}
                      onChange={(e) => {
                        const val = e.target.value;
                        setLocation(val);
                        setLocationDropdownOpen(true);
                        if (locationDebounceRef.current)
                          clearTimeout(locationDebounceRef.current);
                        locationDebounceRef.current = setTimeout(
                          () => searchLocations(val),
                          400
                        );
                      }}
                      onFocus={() => {
                        setLocationDropdownOpen(true);
                        if (!location) detectNearby();
                      }}
                      placeholder={t('social.createPostModal.whereTaken')}
                      className="w-full pl-10 pr-10 py-3 rounded-xl border border-[#E2DFEA] dark:border-white/20 bg-white dark:bg-[#0F0E16] text-[#464452] dark:text-white/90 placeholder:text-[#464452]/50 dark:placeholder:text-white/50 focus:outline-none focus:border-[#936FEF] focus:ring-2 focus:ring-[#936FEF]/20 transition-all"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    />
                    {/* GPS detect button */}
                    <button
                      type="button"
                      onClick={detectAndSetLocation}
                      disabled={geoDetecting}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#936FEF] hover:text-[#2D2F7E] dark:hover:text-white transition-colors disabled:opacity-40"
                      title="Detect my location"
                    >
                      {geoDetecting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Navigation className="w-4 h-4" />
                      )}
                    </button>
                    {/* Suggestion dropdown */}
                    {locationDropdownOpen &&
                      (locationSuggestions.length > 0 || locationLoading) && (
                        <div className="absolute left-0 right-0 top-full mt-1 bg-white dark:bg-[#16151D] border border-[#E2DFEA] dark:border-white/20 rounded-xl shadow-lg z-50 overflow-hidden">
                          {locationLoading ? (
                            <div
                              className="flex items-center gap-2 px-4 py-3 text-sm text-[#464452]/70 dark:text-white/50"
                              style={{ fontFamily: 'Inter, sans-serif' }}
                            >
                              <Loader2 className="w-4 h-4 animate-spin text-[#936FEF]" />
                              Searching nearby places…
                            </div>
                          ) : (
                            locationSuggestions.map((place, i) => (
                              <button
                                key={i}
                                type="button"
                                onClick={() => {
                                  setLocation(place);
                                  setLocationDropdownOpen(false);
                                  setLocationSuggestions([]);
                                }}
                                className="w-full text-left px-4 py-3 text-sm hover:bg-[#F0EFF5] dark:hover:bg-white/10 flex items-center gap-2 text-[#464452] dark:text-white/90 border-b border-[#E2DFEA] dark:border-white/8 last:border-0 transition-colors"
                                style={{ fontFamily: 'Inter, sans-serif' }}
                              >
                                <MapPin className="w-4 h-4 text-[#936FEF] shrink-0" />
                                {place}
                              </button>
                            ))
                          )}
                        </div>
                      )}
                  </div>
                </div>

                {/* Visibility */}
                <div>
                  <label
                    className="block text-sm mb-2 text-[#464452] dark:text-white/70"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('social.whoCanSeeThis')}
                  </label>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <button
                      onClick={() => setVisibility('public')}
                      className={`p-3 rounded-xl border-2 transition-all ${
                        visibility === 'public'
                          ? 'border-[#936FEF] bg-[#F0EFF5] dark:bg-purple-900/20'
                          : 'border-[#E2DFEA] dark:border-white/20'
                      }`}
                    >
                      <Globe className="w-5 h-5 mx-auto mb-1 text-[#936FEF]" />
                      <div
                        className="text-sm text-[#2D2F7E] dark:text-white"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {t('social.public')}
                      </div>
                      <div
                        className="text-xs mt-0.5 text-[#464452] dark:text-white/50 opacity-60"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {t('social.anyone')}
                      </div>
                    </button>
                    <button
                      onClick={() => setVisibility('followers')}
                      className={`p-3 rounded-xl border-2 transition-all ${
                        visibility === 'followers'
                          ? 'border-[#936FEF] bg-[#F0EFF5] dark:bg-purple-900/20'
                          : 'border-[#E2DFEA] dark:border-white/20'
                      }`}
                    >
                      <Users className="w-5 h-5 mx-auto mb-1 text-[#936FEF]" />
                      <div
                        className="text-sm text-[#2D2F7E] dark:text-white"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        Followers
                      </div>
                      <div
                        className="text-xs mt-0.5 text-[#464452] dark:text-white/50 opacity-60"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {t('social.onlyFollowers')}
                      </div>
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <button
                      onClick={() => setVisibility('friends')}
                      className={`p-3 rounded-xl border-2 transition-all ${
                        visibility === 'friends'
                          ? 'border-[#936FEF] bg-[#F0EFF5] dark:bg-purple-900/20'
                          : 'border-[#E2DFEA] dark:border-white/20'
                      }`}
                    >
                      <UserCheck className="w-5 h-5 mx-auto mb-1 text-[#936FEF]" />
                      <div
                        className="text-xs text-[#2D2F7E] dark:text-white"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        Friends
                      </div>
                    </button>
                    <button
                      onClick={() => setVisibility('friends-of-friends')}
                      className={`p-3 rounded-xl border-2 transition-all ${
                        visibility === 'friends-of-friends'
                          ? 'border-[#936FEF] bg-[#F0EFF5] dark:bg-purple-900/20'
                          : 'border-[#E2DFEA] dark:border-white/20'
                      }`}
                    >
                      <UsersRound className="w-5 h-5 mx-auto mb-1 text-[#936FEF]" />
                      <div
                        className="text-xs text-[#2D2F7E] dark:text-white"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {t('social.friendsPlus')}
                      </div>
                    </button>
                    <button
                      onClick={() => setVisibility('private')}
                      className={`p-3 rounded-xl border-2 transition-all ${
                        visibility === 'private'
                          ? 'border-[#936FEF] bg-[#F0EFF5] dark:bg-purple-900/20'
                          : 'border-[#E2DFEA] dark:border-white/20'
                      }`}
                    >
                      <Lock className="w-5 h-5 mx-auto mb-1 text-[#936FEF]" />
                      <div
                        className="text-xs text-[#2D2F7E] dark:text-white"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        Private
                      </div>
                    </button>
                  </div>
                  <p
                    className="text-xs mt-2 text-[#464452] dark:text-white/50 opacity-60"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {visibility === 'public' &&
                      t('social.createPostModal.visibleEveryone')}
                    {visibility === 'followers' &&
                      t('social.createPostModal.visibleFollowers')}
                    {visibility === 'friends' &&
                      t('social.createPostModal.visibleFriends')}
                    {visibility === 'friends-of-friends' &&
                      t('social.createPostModal.visibleFriendsPlus')}
                    {visibility === 'private' &&
                      t('social.createPostModal.visiblePrivate')}
                  </p>
                </div>

                {/* Also share to Your Story */}
                <div className="flex items-center justify-between gap-3 p-4 rounded-xl border border-[#E2DFEA] dark:border-white/20 bg-[#F8F7FA] dark:bg-white/5">
                  <span
                    className="text-[#2D2F7E] dark:text-white"
                    style={{ fontFamily: 'Inter, sans-serif', fontWeight: 500 }}
                  >
                    {t('social.alsoShareToYourStory')}
                  </span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={alsoShareToStory}
                    onClick={() => setAlsoShareToStory((v) => !v)}
                    className={`relative inline-flex h-7 w-12 shrink-0 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#936FEF] focus:ring-offset-2 ${
                      alsoShareToStory
                        ? 'bg-[#936FEF]'
                        : 'bg-[#E2DFEA] dark:bg-white/20'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-6 w-6 rounded-full bg-white shadow ring-0 transition-transform mt-0.5 ${
                        alsoShareToStory ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Professional Tagging (for owners) */}
                {userType === 'owner' && (
                  <div>
                    <label
                      className="block text-sm mb-2 text-[#464452] dark:text-white/70"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {t('social.createPostModal.tagProfessionalsLabel')}
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder={t(
                          'social.createPostModal.searchProfessionals'
                        )}
                        className="flex-1 px-4 py-2 rounded-xl border border-[#E2DFEA] dark:border-white/20 bg-white dark:bg-[#0F0E16] text-[#464452] dark:text-white/90 placeholder:text-[#464452]/50 dark:placeholder:text-white/50"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      />
                    </div>
                    <p
                      className="text-xs mt-1 text-[#464452] dark:text-white/50 opacity-60"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {t('social.createPostModal.tagProfessionalsHint')}
                    </p>
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                {isEditing ? (
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 px-6 py-3 rounded-xl border border-[#E2DFEA] dark:border-white/20 text-[#464452] dark:text-white/70 hover:bg-[#F0EFF5] dark:hover:bg-white/5 transition-colors"
                    style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                  >
                    {t('common.cancel', 'Cancel')}
                  </button>
                ) : (
                  <button
                    onClick={() => setStep('content')}
                    className="flex-1 px-6 py-3 rounded-xl border border-[#E2DFEA] dark:border-white/20 text-[#464452] dark:text-white/70 hover:bg-[#F0EFF5] dark:hover:bg-white/5 transition-colors"
                    style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                  >
                    {t('common.back')}
                  </button>
                )}
                <button
                  type="button"
                  onClick={isEditing ? handleUpdate : handlePost}
                  disabled={uploading}
                  className="flex-1 px-6 py-3 rounded-xl text-white hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{
                    background:
                      'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
                    fontFamily: 'Sora, sans-serif',
                    fontWeight: 700,
                  }}
                >
                  {uploading
                    ? t('common.loading')
                    : isEditing
                      ? t('social.saveChanges', 'Save Changes')
                      : t('social.createPostModal.postButton')}
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Media Editor (Crop & Filters) */}
      {editingMediaIndex !== null &&
        selectedMediaUrls[editingMediaIndex] &&
        contentType === 'photo' && (
          <MediaEditorModal
            imageUrl={selectedMediaUrls[editingMediaIndex]!}
            isVideo={false}
            onClose={() => setEditingMediaIndex(null)}
            onDone={handleMediaEditDone(editingMediaIndex)}
          />
        )}
    </div>
  );
}
